import { PersonTitle } from '@app/features/employees/employee.model';
import { Schedule } from '../horaires/schedules.model';
import { PostalAddress } from './postal-adress.model';

/**
 * L'état d'une société, tel que le tient le registre des entités légales : une société cédée ou
 * dissoute reste affichée sur les contrats qui la citent, mais n'est plus proposée pour un
 * nouveau contrat.
 */
export type CompanyState = 'ACTIVE' | 'SOLD' | 'DISSOLVED';

/**
 * Une société du groupe, copiée par l'API du registre des entités légales. Le registre tient le
 * code, la raison sociale (`name`), le nom court, la forme juridique, le SIREN, le SIRET, le RCS,
 * le siège, le dossier du pôle social, l'état et le logo ; CDT ne modifie que le reste.
 */
export interface Company {
  id: string;
  code?: string;
  /** La raison sociale. */
  name: string;
  shortName?: string | null;
  state?: CompanyState;
  /** La date de la cession ou de la dissolution, `AAAA-MM-JJ`. */
  stateDate?: string | null;
  collectiveSchedules: Schedule[];
  companyColor: string;
  legalInformation: LegalInformation;
  phoneNumber: string;
  email: string;
  /**
   * Null quand aucune de leurs colonnes n'est remplie : une société créée hors de la fiche, par
   * le jeu de données de performance par exemple, n'en a pas.
   */
  retraiteComplementaire: PostalAddress | null;
  prevoyance: PostalAddress | null;
  /**
   * Le nom du dossier de la société dans POLE SOCIAL, sur le disque réseau de Vanex, préfixe de
   * rang compris (ex. « 7_VB Propreté »). Tenu par le registre des entités légales
   * (RG-dossier-du-pole-social) : CDT l'affiche sans le modifier.
   */
  vanexNetworkFolderName?: string | null;
}

export interface LegalInformation {
  address: string;
  postalCode: string;
  city: string;
  siret: string;
  siren?: string | null;
  legalRepresentative: string;
  legalRepresentativePosition: string;
  legalRepresentativeTitle: PersonTitle;
  legalForm: string;
  capital: number;
  rcs: string;
}

export interface CompanyLogoMetadata {
  companyId: string;
  hash: string;
  mimeType: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

/** Vrai tant que la société peut porter un nouveau contrat : un état inconnu vaut « active ». */
export function isOpenToNewContracts(company: Company): boolean {
  return company.state === undefined || company.state === 'ACTIVE';
}

/** « Cédée le 31/10/2026 », « Dissoute le … », rien pour une société active. */
export function companyStateLabel(company: Company): string | null {
  if (isOpenToNewContracts(company)) {
    return null;
  }
  const label = company.state === 'SOLD' ? 'Cédée' : 'Dissoute';
  if (!company.stateDate) {
    return label;
  }
  const [year, month, day] = company.stateDate.split('-');
  return `${label} le ${day}/${month}/${year}`;
}
